import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { ImageLightbox } from './ImageLightbox';
import CollectibilityPanel from './CollectibilityPanel';
import VehicleBuildViews from './VehicleBuildViews';
import { VehicleHeroGallery } from './vehicle-detail/VehicleHeroGallery';
import type { HeroImage } from './vehicle-detail/types';
import type { CollectibilityData } from '../types/collectibility';
import { resolvePublicFileUrl } from '../utils/imageUrls';
import { formatCurrency } from '../utils/formatCurrency';
import { useShowcaseMode } from '../hooks/useShowcaseMode';
import { updateMetaTags } from '../utils/metaTags';
import PublicVehiclePageSkeleton from './ui/PublicVehiclePageSkeleton';
import Footer from './Footer';
import ExteriorFinishDisplay from './vehicle/ExteriorFinishDisplay';
import VehicleBuildSpecs from './vehicle/VehicleBuildSpecs';
import BuildTimeline from './vehicle/BuildTimeline';
import VehiclePassportStrip from './passport/VehiclePassportStrip';
import VehiclePassportOwnerPanel from './passport/VehiclePassportOwnerPanel';
import OwnerOtherBuilds from './vehicle/OwnerOtherBuilds';
import VehicleSectionNav from './vehicle/VehicleSectionNav';
import VehicleRecognitionSection from './vehicle/VehicleRecognitionSection';
import VehicleSharingModal from './VehicleSharingModal';
import type { VehicleHistoryEntry } from '../types/vehicleHistory';
import { BuiltLogoMark } from './ui/BuiltLogoMark';
import type { VehicleCompletenessResult } from '../utils/vehicleCompleteness';
import { ShareIcon, QrCodeIcon, LinkIcon, CheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import FavoriteBuildButton from './FavoriteBuildButton';
import LikeBuildButton from './LikeBuildButton';
import BuildWatchButton from './BuildWatchButton';
import BuildEngagementBar from './BuildEngagementBar';
import BuildViewTracker from './BuildViewTracker';
import type { BuildPageExpansion, BuildProfile, ExpansionFeatures } from '../types/buildExpansion';
import {
  AchievementCollection,
  BuildStatsStrip,
  ContributorCards,
  DocumentationScore,
  DynoHistory,
  FactoryCurrentComparison,
  MediaAlbums,
  OwnerIdentityCard,
  PerformanceRecords,
} from './expansion';
import {
  AlbumManagerModal,
  BuildProfileEditorModal,
  ContributorRequestModal,
  MediaLinkManagerModal,
  PerformanceRecordManager,
} from './expansion/owner';
import ShareCardGenerator from './share/ShareCardGenerator';
import QrStickerGenerator from './share/QrStickerGenerator';
import QrPrintKitPanel from './share/QrPrintKitPanel';
import { Modal } from './ui/Modal';
import { AddModificationModal } from './AddModificationForm';
import { EditModificationModal } from './EditModificationModal';
import EditVehicleModal from './EditVehicleModal';
import { VehicleImageUploadModal } from './VehicleImageUploadModal';
import { ImportModificationsModal } from './ImportModificationsModal';
import { EnhanceModificationsModal } from './EnhanceModificationsModal';
import { SettingsModal } from './SettingsModal';
import { SettingsData } from '../types/settings';
import { mapModificationsWithParent } from '../utils/modifications';
import { VehicleOwnerToolbar } from './vehicle-detail/VehicleOwnerToolbar';
import { ImageTitleEditModal } from './vehicle-detail/ImageTitleEditModal';
import type { VehicleDetailModification } from './vehicle-detail/types';
import AdminEditingBanner from './ui/AdminEditingBanner';
import type { Vehicle } from '../types/vehicle';
import FeatureTip from './onboarding/FeatureTip';


interface PublicVehicleData {
  vehicle: {
    id: number;
    year: number;
    make: string;
    model: string;
    trim: string;
    color: string;
    exterior_finish_type?: 'paint' | 'wrap' | 'ppf';
    exterior_finish_color?: string | null;
    exterior_finish_brand?: string | null;
    exterior_finish_style?: string | null;
    chassis?: string;
    engine?: string;
    transmission?: string;
    drivetrain?: string;
    build_status?: 'planned' | 'in_progress' | 'complete' | null;
    nickname: string;
    description: string;
    slug: string;
    created_at: string;
    theme_color: string;
    custom_message: string;
  };
  is_owner: boolean;
  can_edit?: boolean;
  is_super_admin_edit?: boolean;
  profileCompletion: VehicleCompletenessResult;
  owner: {
    username: string;
    display_name: string;
    profile_image: string;
  };
  settings: {
    show_modifications: boolean;
    show_performance: boolean;
    show_images: boolean;
    show_contact_info: boolean;
    show_social_links: boolean;
  };
  modifications: Array<{
    id: string;
    title: string;
    description?: string;
    category: string;
    vendor?: string;
    cost?: number;
    install_date?: string;
    notes?: string;
    part_number?: string;
    power_gain?: number;
    torque_gain?: number;
    weight_change?: number;
    parent_modification_id?: string | number | null;
    parentComponent?: string | null;
    sort_order?: number | null;
    specifications?: string[] | Record<string, unknown> | string | null;
    image_url?: string | null;
    image_id?: number | null;
  }>;
  images: Array<{
    id?: number;
    image_url: string;
    image_type?: string;
    title?: string;
    alt_text?: string;
  }>;
  performance: {
    horsepower?: number | null;
    torque?: number | null;
    redline?: number | null;
  } | null;
  stats: {
    total_views?: number;
    total_shares?: number;
    last_viewed?: string | null;
    view_count?: number;
    favorite_count?: number;
    like_count?: number;
  };
  favorite_count?: number;
  is_favorited?: boolean;
  like_count?: number;
  user_has_liked?: boolean;
  view_count?: number;
  collectibility?: CollectibilityData;
  awards?: Array<{
    id: number;
    title: string;
    placement: string | null;
    event_title: string | null;
    event_slug: string | null;
    published_at: string | null;
  }>;
  expansion?: BuildPageExpansion;
  expansion_features?: ExpansionFeatures;
  meta: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

/** Approved build-profile accents → CSS hex (WCAG AA vs gray-950 panels). */
const BUILD_ACCENT_HEX: Record<string, string> = {
  orange: '#f97316',
  red: '#ef4444',
  amber: '#f59e0b',
  emerald: '#10b981',
  sky: '#0ea5e9',
  violet: '#8b5cf6',
  rose: '#f43f5e',
  slate: '#94a3b8',
};

const PURPOSE_LABELS: Record<string, string> = {
  street: 'Street',
  daily: 'Daily',
  track: 'Track',
  time_attack: 'Time attack',
  drag: 'Drag',
  drift: 'Drift',
  rally: 'Rally',
  autocross: 'Autocross',
  show: 'Show',
  stance: 'Stance',
  off_road: 'Off-road',
  overland: 'Overland',
  restoration: 'Restoration',
  oem_plus: 'OEM+',
  touring: 'Touring',
  competition: 'Competition',
  mixed_use: 'Mixed use',
};

const COMPLETION_LABELS: Record<string, string> = {
  engine: 'engine',
  transmission: 'transmission',
  drivetrain: 'drivetrain',
  chassis: 'chassis',
  description: 'a description',
  photo: 'a photo',
  modification: 'a modification',
};

const VehiclePage: React.FC = () => {
  const params = useParams<{ username?: string; slug?: string; vehicleName?: string; vehicleSlug?: string }>();
  const username = params.username;
  const slug = params.slug || params.vehicleSlug || params.vehicleName;
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get('ref');
  const showcaseMode = useShowcaseMode();

  const [vehicleData, setVehicleData] = useState<PublicVehicleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [history, setHistory] = useState<VehicleHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [editableMods, setEditableMods] = useState<VehicleDetailModification[]>([]);
  const [allVendors, setAllVendors] = useState<{ name: string }[]>([]);
  const [heroImages, setHeroImages] = useState<HeroImage[]>([]);
  const [isAddModificationModalOpen, setIsAddModificationModalOpen] = useState(false);
  const [addModificationParent, setAddModificationParent] = useState<{
    id: string;
    title: string;
    category?: string;
  } | null>(null);
  const [organizeSelectMode, setOrganizeSelectMode] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isImportModificationsModalOpen, setIsImportModificationsModalOpen] = useState(false);
  const [isEnhanceModalOpen, setIsEnhanceModalOpen] = useState(false);
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [isEditModificationModalOpen, setIsEditModificationModalOpen] = useState(false);
  const [editingModification, setEditingModification] = useState<VehicleDetailModification | null>(null);
  const [deleteImageConfirm, setDeleteImageConfirm] = useState<number | null>(null);
  const [imageTitleEditIndex, setImageTitleEditIndex] = useState<number | null>(null);
  const [displaySettings, setDisplaySettings] = useState<SettingsData>({
    showPricing: true,
    showPerformance: true,
  });
  const [liveFavoriteCount, setLiveFavoriteCount] = useState(0);
  const [liveLikeCount, setLiveLikeCount] = useState(0);
  const [liveIsFavorited, setLiveIsFavorited] = useState(false);
  const [perfManagerOpen, setPerfManagerOpen] = useState(false);
  const [albumManagerOpen, setAlbumManagerOpen] = useState(false);
  const [mediaLinkOpen, setMediaLinkOpen] = useState(false);
  const [profileEditorOpen, setProfileEditorOpen] = useState(false);
  const [contributorRequestOpen, setContributorRequestOpen] = useState(false);
  const [shareCardOpen, setShareCardOpen] = useState(false);
  const [qrStickerOpen, setQrStickerOpen] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [qrPrintKitEnabled, setQrPrintKitEnabled] = useState(false);

  useEffect(() => {
    const loadVehicleData = async () => {
      if (!username || !slug) {
        setError('Invalid vehicle link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getPublicVehicle(username, slug, referrer || undefined);
        setVehicleData(data);
        setLiveFavoriteCount(
          Number(data.favorite_count ?? data.stats?.favorite_count ?? 0)
        );
        setLiveLikeCount(Number(data.like_count ?? data.stats?.like_count ?? 0));
        setLiveIsFavorited(Boolean(data.is_favorited));
        setDisplaySettings({
          showPricing: true,
          showPerformance: data.settings.show_performance !== false,
        });

        const modCount = data.modifications?.length ?? 0;
        // Lead the share description with the build + owner. Rarity is a
        // secondary detail on Bilt, so it is intentionally not emphasized here.
        const metaDescription = data.meta.description || [
          modCount > 0 ? `${modCount} mods` : null,
          `by ${data.owner.display_name || data.owner.username}`,
        ].filter(Boolean).join(' · ');

        updateMetaTags({
          title: data.meta.title.includes('| Bilt') || data.meta.title.includes('| Built')
            ? data.meta.title.replace('| Built', '| Bilt')
            : `${data.meta.title} | Bilt`,
          description: metaDescription,
          image: data.meta.image || `${window.location.origin}/logo512.png`,
          url: data.meta.url,
          type: 'website',
        });
      } catch (err: unknown) {
        console.error('Failed to load public vehicle data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load vehicle data');
      } finally {
        setLoading(false);
      }
    };

    loadVehicleData();
  }, [username, slug, referrer]);

  const reloadHistory = useCallback(async () => {
    if (!username || !slug) return;
    try {
      setHistoryLoading(true);
      const entries = await apiService.getVehicleHistory({ username, slug });
      setHistory(entries);
    } catch (err) {
      console.error('Failed to load vehicle activity', err);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [username, slug]);

  useEffect(() => {
    if (!username || !slug || !vehicleData) return;
    const timer = window.setTimeout(() => {
      void reloadHistory();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [username, slug, vehicleData, reloadHistory]);

  const galleryImages: HeroImage[] = useMemo(() => {
    if (!vehicleData?.images?.length) return [];
    const label = `${vehicleData.vehicle.year} ${vehicleData.vehicle.make} ${vehicleData.vehicle.model}`;
    return vehicleData.images.map((img, index) => ({
      id: String(img.id ?? index),
      src:
        resolvePublicFileUrl(img.image_url) ||
        apiService.getFileUrl(img.image_url) ||
        img.image_url,
      alt: img.alt_text || `${label} photo ${index + 1}`,
      title: img.title || `Photo ${index + 1}`,
    }));
  }, [vehicleData]);

  const totalModCost = useMemo(() => {
    const mods =
      editableMods.length > 0 ? editableMods : vehicleData?.modifications || [];
    return mods.reduce((sum, mod) => sum + (mod.cost || 0), 0);
  }, [vehicleData, editableMods]);


  // Keep local heroImages in sync for CRUD when editing
  useEffect(() => {
    setHeroImages(galleryImages);
  }, [galleryImages]);

  const refreshVendors = useCallback(async () => {
    try {
      const vendorsData = await apiService.getVendors();
      setAllVendors(vendorsData.map((v: { name: string }) => ({ name: v.name })));
    } catch (err) {
      console.error('Failed to refresh vendors:', err);
    }
  }, []);

  const reloadEditableMods = useCallback(async (vehicleId: number) => {
    const raw = await apiService.getModifications(vehicleId);
    const mapped = mapModificationsWithParent(raw);
    setEditableMods(mapped);
    // Keep public vehicle payload in sync so mod photos survive view switches / refresh paths.
    setVehicleData((prev) => {
      if (!prev || prev.vehicle.id !== vehicleId) return prev;
      return {
        ...prev,
        modifications: mapped.map((mod) => ({
          ...mod,
          image_url: resolvePublicFileUrl(mod.image_url) ?? mod.image_url ?? null,
          image_id: mod.image_id ?? null,
        })),
      };
    });
  }, []);

  const canEdit = !!(
    vehicleData &&
    (vehicleData.can_edit === true ||
      (vehicleData.is_super_admin_edit === true) ||
      (isAuthenticated && user && vehicleData.is_owner))
  );

  useEffect(() => {
    if (!vehicleData || !canEdit) {
      setEditableMods([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await reloadEditableMods(vehicleData.vehicle.id);
        await refreshVendors();
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleData, canEdit, reloadEditableMods, refreshVendors]);

  const isAnyModalOpen =
    isAddModificationModalOpen ||
    isEditVehicleModalOpen ||
    isSettingsModalOpen ||
    isImportModificationsModalOpen ||
    isEnhanceModalOpen ||
    isImageUploadModalOpen ||
    isEditModificationModalOpen ||
    shareModalOpen ||
    perfManagerOpen ||
    albumManagerOpen ||
    mediaLinkOpen ||
    profileEditorOpen ||
    contributorRequestOpen ||
    shareCardOpen ||
    qrStickerOpen ||
    deleteImageConfirm !== null ||
    imageTitleEditIndex !== null;

  const handleEditModification = (modification: VehicleDetailModification) => {
    setEditingModification(modification);
    setIsEditModificationModalOpen(true);
  };

  const handleDeleteModification = async (modificationId: string) => {
    if (!canEdit || !vehicleData) return;
    try {
      const targetMod = editableMods.find((m) => m.id === modificationId);
      if (!targetMod) return;
      const childMods = editableMods.filter(
        (m) =>
          (m.parent_modification_id && String(m.parent_modification_id) === String(modificationId)) ||
          (m.parentComponent && m.parentComponent === targetMod.title)
      );
      let childPolicy: 'reject' | 'promote' | 'cascade' = 'reject';
      if (childMods.length > 0) {
        const choice = window.prompt(
          `"${targetMod.title}" has ${childMods.length} nested part(s).\n` +
            'Type promote (move children up), cascade (delete all nested), or cancel.',
          'promote'
        );
        if (!choice || choice.toLowerCase() === 'cancel') return;
        const normalized = choice.toLowerCase().trim();
        if (normalized !== 'promote' && normalized !== 'cascade') {
          showToast('Delete cancelled. Enter promote or cascade.', 'error');
          return;
        }
        childPolicy = normalized as 'promote' | 'cascade';
      }
      await apiService.deleteModification(parseInt(modificationId, 10), { childPolicy });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Modification deleted', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to delete modification.';
      showToast(msg, 'error');
    }
  };

  const handleModificationAdded = async (
    newModification: Record<string, unknown>,
    photoFile?: File | null
  ) => {
    if (!vehicleData) return;
    try {
      let modificationData: Record<string, unknown> = { ...newModification };
      const parentIdFromForm =
        newModification.parent_modification_id != null
          ? Number(newModification.parent_modification_id)
          : NaN;
      if (!Number.isNaN(parentIdFromForm) && parentIdFromForm > 0) {
        modificationData.parent_modification_id = parentIdFromForm;
      } else if (newModification.parentComponent) {
        const parentMod = editableMods.find((mod) => mod.title === newModification.parentComponent);
        if (parentMod) {
          modificationData.parent_modification_id = parseInt(parentMod.id, 10);
        }
      }
      const created = await apiService.createModification(vehicleData.vehicle.id, modificationData);
      const createdId = Number(created?.id);
      if (photoFile && Number.isFinite(createdId) && createdId > 0) {
        try {
          await apiService.uploadVehicleImage(
            vehicleData.vehicle.id,
            photoFile,
            'modification',
            createdId
          );
        } catch {
          showToast('Part added, but photo upload failed. Edit the part to retry.', 'error');
        }
      }
      await reloadEditableMods(vehicleData.vehicle.id);
      setIsAddModificationModalOpen(false);
      setAddModificationParent(null);
      showToast('Modification added', 'success');
      await refreshVendors();
    } catch {
      showToast('Failed to add modification. Please try again.', 'error');
    }
  };

  const handleModificationUpdated = async (
    updatedModification: VehicleDetailModification,
    photoFile?: File | null
  ) => {
    if (!vehicleData) return;
    try {
      const payload: Record<string, unknown> = { ...updatedModification };
      if (updatedModification.parentComponent) {
        const parentMod = editableMods.find((mod) => mod.title === updatedModification.parentComponent);
        if (parentMod) {
          payload.parent_modification_id = parseInt(parentMod.id, 10);
          payload.cost = 0;
        }
      } else {
        payload.parent_modification_id = null;
      }
      const modId = parseInt(updatedModification.id, 10);
      await apiService.updateModification(modId, payload);
      if (photoFile && Number.isFinite(modId) && modId > 0) {
        try {
          await apiService.uploadVehicleImage(
            vehicleData.vehicle.id,
            photoFile,
            'modification',
            modId
          );
        } catch {
          showToast('Part updated, but photo upload failed. Try again from Edit.', 'error');
        }
      }
      await reloadEditableMods(vehicleData.vehicle.id);
      setIsEditModificationModalOpen(false);
      setEditingModification(null);
      showToast('Modification updated', 'success');
      await refreshVendors();
    } catch {
      showToast('Failed to update modification.', 'error');
    }
  };

  const openAddModification = (parent?: VehicleDetailModification | null) => {
    setAddModificationParent(
      parent
        ? { id: String(parent.id), title: parent.title, category: parent.category }
        : null
    );
    setIsAddModificationModalOpen(true);
  };

  const handleOrganizeMove = async (args: {
    modificationId: number;
    parentModificationId: number | null;
    category?: string;
    beforeModificationId?: number | null;
    afterModificationId?: number | null;
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'move',
        modification_id: args.modificationId,
        parent_modification_id: args.parentModificationId,
        category: args.category,
        before_modification_id: args.beforeModificationId,
        after_modification_id: args.afterModificationId,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Part moved', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not move that part.';
      showToast(msg, 'error');
      throw error;
    }
  };

  const handleOrganizeReorder = async (args: {
    parentModificationId: number | null;
    orderedIds: number[];
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'reorder',
        parent_modification_id: args.parentModificationId,
        ordered_ids: args.orderedIds,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Order updated', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not reorder parts.';
      showToast(msg, 'error');
    }
  };

  const handleOrganizeBulkMove = async (args: {
    modificationIds: number[];
    parentModificationId: number | null;
    category?: string;
    beforeModificationId?: number | null;
    afterModificationId?: number | null;
  }) => {
    if (!vehicleData) return;
    try {
      const data = await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'bulk_move',
        modification_ids: args.modificationIds,
        parent_modification_id: args.parentModificationId,
        category: args.category,
        before_modification_id: args.beforeModificationId,
        after_modification_id: args.afterModificationId,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      const moved = typeof data?.moved === 'number' ? data.moved : args.modificationIds.length;
      showToast(moved === 1 ? 'Moved 1 part' : `Moved ${moved} parts`, 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not move selected parts.';
      showToast(msg, 'error');
      throw error;
    }
  };

  const handleOrganizeBulkCategory = async (args: {
    modificationIds: number[];
    category: string;
    includeNested: boolean;
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'bulk_category',
        modification_ids: args.modificationIds,
        category: args.category,
        include_nested: args.includeNested,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Category updated', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not change category.';
      showToast(msg, 'error');
    }
  };

  const handleOrganizeBulkDelete = async (args: {
    modificationIds: number[];
    childPolicy: 'promote' | 'cascade';
  }) => {
    if (!vehicleData) return;
    try {
      await apiService.organizeModifications(vehicleData.vehicle.id, {
        action: 'bulk_delete',
        modification_ids: args.modificationIds,
        child_policy: args.childPolicy,
      });
      await reloadEditableMods(vehicleData.vehicle.id);
      showToast('Parts deleted', 'success');
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Could not delete selected parts.';
      showToast(msg, 'error');
    }
  };

  const handleSettingsSave = async (newSettings: SettingsData) => {
    if (!vehicleData) return;
    await apiService.updateVehicleSettings(vehicleData.vehicle.id, {
      show_pricing: newSettings.showPricing ?? true,
      show_performance: newSettings.showPerformance ?? true,
    });
    setDisplaySettings(newSettings);
    showToast('Display settings saved', 'success');
  };

  const handleDeleteVehicle = async () => {
    if (!vehicleData || !username) return;
    await apiService.deleteVehicle(vehicleData.vehicle.id);
    showToast('Vehicle deleted', 'success');
    window.location.href = `/${username}`;
  };

  const handleVehicleUpdated = (updated: Vehicle) => {
    setVehicleData((prev) =>
      prev
        ? {
            ...prev,
            vehicle: {
              ...prev.vehicle,
              year: updated.year,
              make: updated.make,
              model: updated.model,
              trim: updated.trim || '',
              color: updated.color || '',
              nickname: updated.nickname || '',
              description: updated.description || '',
              chassis: updated.chassis,
              engine: updated.engine,
              transmission: updated.transmission,
              drivetrain: updated.drivetrain,
              build_status: updated.build_status,
            },
          }
        : prev
    );
    setIsEditVehicleModalOpen(false);
  };

  const reloadGallery = useCallback(async () => {
    if (!vehicleData) return;
    const imagesData = await apiService.getVehicleImages(vehicleData.vehicle.id);
    const label = `${vehicleData.vehicle.year} ${vehicleData.vehicle.make} ${vehicleData.vehicle.model}`;
    const next = imagesData
      .filter((img: { image_type?: string }) => img.image_type === 'hero' || img.image_type === 'gallery' || !img.image_type)
      .map((img: { id: number; image_url: string; alt_text?: string; title?: string }, index: number) => ({
        id: String(img.id),
        src: resolvePublicFileUrl(img.image_url) || apiService.getFileUrl(img.image_url) || img.image_url,
        alt: img.alt_text || `${label} photo ${index + 1}`,
        title: img.title || `Photo ${index + 1}`,
      }));
    setHeroImages(next);
  }, [vehicleData]);

  const handleSaveImageTitle = async (newTitle: string) => {
    if (imageTitleEditIndex === null) return;
    const image = heroImages[imageTitleEditIndex];
    if (!image?.id) throw new Error('Image ID not found');
    await apiService.updateVehicleImage(parseInt(image.id, 10), { title: newTitle });
    setHeroImages((prev) => {
      const copy = [...prev];
      copy[imageTitleEditIndex] = { ...image, title: newTitle };
      return copy;
    });
    showToast('Image title updated', 'success');
  };

  const confirmDeleteImage = async () => {
    if (deleteImageConfirm === null) return;
    const index = deleteImageConfirm;
    setDeleteImageConfirm(null);
    try {
      const image = heroImages[index];
      if (!image?.id) throw new Error('Image ID not found');
      await apiService.deleteVehicleImage(parseInt(image.id, 10));
      setHeroImages((prev) => prev.filter((_, i) => i !== index));
    } catch {
      showToast('Failed to delete image. Please try again.', 'error');
    }
  };

  const handleMoveImage = async (fromIndex: number, toIndex: number) => {
    if (!canEdit || fromIndex === toIndex) return;
    const next = [...heroImages];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setHeroImages(next);
    try {
      await Promise.all(
        next.map((img, order) =>
          img.id
            ? apiService.updateVehicleImage(parseInt(img.id, 10), { sort_order: order })
            : Promise.resolve()
        )
      );
    } catch {
      showToast('Failed to reorder photos', 'error');
      await reloadGallery();
    }
  };

  const handleImageUploaded = async () => {
    setImageUploadError(null);
    try {
      await reloadGallery();
      showToast('Photo uploaded', 'success');
    } catch {
      showToast('Upload succeeded but gallery failed to refresh. Reload the page.', 'error');
    }
  };

  const isOwnerFlag = Boolean(vehicleData?.is_owner);
  useEffect(() => {
    if (!isOwnerFlag) {
      setQrPrintKitEnabled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const flags = await apiService.getPaidFeatureFlags();
        if (!cancelled) setQrPrintKitEnabled(Boolean(flags.qr_print_kit_enabled));
      } catch {
        if (!cancelled) setQrPrintKitEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwnerFlag]);

  // Prefetch branded monochrome QR for owner print kit / share tools.
  useEffect(() => {
    if (!isOwnerFlag || showcaseMode || !vehicleData?.vehicle?.id || qrImageUrl) return;
    const shareEnabled = vehicleData.expansion_features?.share_cards_enabled !== false;
    if (!qrPrintKitEnabled && !shareEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const qr = await apiService.generateQRCode(vehicleData.vehicle.id, {
          format: 'svg',
          size: 'large',
        });
        if (cancelled) return;
        const rawPath = qr.qr_code_url || qr.file_path || '';
        const serveMatch = rawPath.match(/serve_qr\.php\?file=[^&]+/);
        const relativePath = serveMatch ? serveMatch[0] : rawPath;
        const url = apiService.getFileUrl(relativePath) || relativePath || null;
        if (url) setQrImageUrl(url);
      } catch {
        // Best-effort prefetch; share tools regenerate on demand.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOwnerFlag,
    showcaseMode,
    vehicleData?.vehicle?.id,
    vehicleData?.expansion_features?.share_cards_enabled,
    qrPrintKitEnabled,
    qrImageUrl,
  ]);

  if (loading) {
    return <PublicVehiclePageSkeleton />;
  }

  if (error || !vehicleData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="card-elevated max-w-md w-full p-8 text-center">
          <BuiltLogoMark size="lg" className="mx-auto mb-4" />
          <h1 className="heading-section mb-2">Build not found</h1>
          <p className="text-muted mb-6 text-sm leading-relaxed">
            {error || 'This link may be private or no longer available.'}
          </p>
          <Link to="/discover" className="btn-primary">
            Browse builds
          </Link>
        </div>
      </div>
    );
  }

  const { vehicle, owner, settings, modifications: publicMods, performance, stats, collectibility, awards, is_owner: isOwner, profileCompletion } = vehicleData;
  const modifications = canEdit && editableMods.length > 0 ? editableMods : publicMods;
  const hasModCosts = modifications.some((m) => m.cost != null && m.cost > 0);
  const showPricing = (displaySettings.showPricing !== false) && hasModCosts;
  const activeGallery = canEdit && heroImages.length > 0 ? heroImages : galleryImages;
  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}`;
  const showGallery = settings.show_images && (activeGallery.length > 0 || canEdit);
  const shareUrl = vehicleData.meta.url;
  const viewCount =
    vehicleData.view_count ??
    stats?.view_count ??
    stats?.total_views ??
    0;
  const favoriteCount = liveFavoriteCount;
  const isFavorited = liveIsFavorited;
  const likeCount = liveLikeCount;
  const isLiked = Boolean(vehicleData.user_has_liked);
  const expansion = vehicleData.expansion;
  const shareCardsEnabled = vehicleData.expansion_features?.share_cards_enabled !== false;
  const achievements = expansion?.achievements ?? [];
  const milestoneCount = Math.max(
    Array.isArray(awards) ? awards.length : 0,
    achievements.filter((a) => a.status === 'current').length
  );
  const buildSummary = expansion?.build_summary ?? null;
  const buildProfile = expansion?.build_profile ?? null;
  const accentHex =
    (buildProfile?.accent_color && BUILD_ACCENT_HEX[buildProfile.accent_color]) || null;
  const heroTreatment = buildProfile?.hero_treatment === 'light' ? 'light' : 'dark';
  const heroPlacement = buildProfile?.hero_text_placement || 'bottom_left';
  const purposeChips: string[] = [];
  if (buildProfile?.purpose_primary) purposeChips.push(buildProfile.purpose_primary);
  for (const purpose of buildProfile?.purposes ?? []) {
    if (purpose && !purposeChips.includes(purpose)) purposeChips.push(purpose);
  }
  const nonDynoRecords = (expansion?.performance_records ?? []).filter((r) => r.record_type !== 'dyno');
  const galleryForManagers = activeGallery.flatMap((img) => {
    const numericId = Number(img.id);
    if (!Number.isFinite(numericId) || numericId <= 0) return [];
    return [{ id: numericId, image_url: img.src, title: img.title || null }];
  });
  const modsForManagers = modifications.map((m) => ({
    id: m.id,
    title: m.title,
  }));

  const showBuildSection = (settings.show_modifications || canEdit) && (modifications.length > 0 || canEdit);
  const showPerformanceSection = nonDynoRecords.length > 0 || !!expansion?.dyno_summary || !!canEdit;
  const showTimelineSection = history.length > 0 || !!canEdit;
  const showMediaSection = (expansion?.media_albums?.length ?? 0) > 0;

  // Core IA: Overview → Recognition → Performance → Build → Timeline (+ Media when present).
  const sectionNavItems = [
    { id: 'overview', label: 'Overview' },
    ...(vehicle?.id || canEdit ? [{ id: 'recognition', label: 'Recognition' }] : []),
    ...(showPerformanceSection ? [{ id: 'performance', label: 'Performance' }] : []),
    ...(showBuildSection ? [{ id: 'build', label: 'Build' }] : []),
    ...(showMediaSection ? [{ id: 'media', label: 'Media' }] : []),
    ...(showTimelineSection ? [{ id: 'timeline', label: 'Timeline' }] : []),
  ];

  const refreshExpansion = async () => {
    if (!username || !slug) return;
    try {
      const data = await apiService.getPublicVehicle(username, slug, referrer || undefined);
      setVehicleData(data);
      setLiveFavoriteCount(Number(data.favorite_count ?? data.stats?.favorite_count ?? 0));
      setLiveIsFavorited(Boolean(data.is_favorited));
    } catch {
      // Best-effort refresh after owner edits.
    }
  };

  const ensureQrImage = async (): Promise<string | null> => {
    if (qrImageUrl) return qrImageUrl;
    try {
      const qr = await apiService.generateQRCode(vehicle.id, { format: 'svg', size: 'large' });
      const rawPath = qr.qr_code_url || qr.file_path || '';
      const serveMatch = rawPath.match(/serve_qr\.php\?file=[^&]+/);
      const relativePath = serveMatch ? serveMatch[0] : rawPath;
      const url = apiService.getFileUrl(relativePath) || relativePath || null;
      if (url) setQrImageUrl(url);
      return url;
    } catch {
      showToast('Could not generate QR image', 'error');
      return null;
    }
  };

  const openShareCard = async () => {
    await ensureQrImage();
    setShareCardOpen(true);
  };

  const openQrSticker = async () => {
    setQrStickerOpen(true);
    await ensureQrImage();
  };

  const ownerToolActions = canEdit && !showcaseMode
    ? [
        { id: 'build-profile', label: 'Build profile', onClick: () => setProfileEditorOpen(true) },
        { id: 'performance-records', label: 'Performance records', onClick: () => setPerfManagerOpen(true) },
        { id: 'media-albums', label: 'Media albums', onClick: () => setAlbumManagerOpen(true) },
        { id: 'link-media', label: 'Link media to mods', onClick: () => setMediaLinkOpen(true) },
        { id: 'add-contributor', label: 'Add contributor', onClick: () => setContributorRequestOpen(true) },
        ...(shareCardsEnabled
          ? [
              { id: 'share-card', label: 'Share card', onClick: () => { void openShareCard(); } },
              { id: 'qr-sticker', label: 'QR sticker', onClick: () => { void openQrSticker(); } },
            ]
          : []),
        { id: 'edit-vehicle', label: 'Edit vehicle', onClick: () => setIsEditVehicleModalOpen(true) },
        { id: 'settings', label: 'Build settings', onClick: () => setIsSettingsModalOpen(true) },
      ]
    : [];

  const applyBuildProfile = (profile: BuildProfile | null) => {
    setVehicleData((prev) =>
      prev
        ? {
            ...prev,
            expansion: {
              ...(prev.expansion || {
                build_profile: null,
                build_summary: null,
                stats_strip: [],
                power_to_weight: null,
                factory_comparison: null,
                achievements: [],
                documentation_score: null,
                performance_records: [],
                dyno_summary: null,
                media_albums: [],
                media_links: [],
                contributors: [],
              }),
              build_profile: profile,
              build_summary: profile?.summary || prev.expansion?.build_summary || null,
            },
          }
        : prev
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context); silently ignore.
    }
  };

  const handleShare = async () => {
    // Prefer the native share sheet on supporting devices; otherwise copy the link.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: vehicleData.meta.title,
          text: vehicleData.meta.description,
          url: shareUrl,
        });
        return;
      } catch {
        // User cancelled or share failed — fall back to copying the link.
      }
    }
    await handleCopyLink();
  };

  const galleryBlock = showGallery ? (
    <VehicleHeroGallery
      images={activeGallery}
      isOwner={canEdit}
      onImageClick={setLightboxIndex}
      onEditImage={canEdit ? (index) => setImageTitleEditIndex(index) : () => undefined}
      onDeleteImage={canEdit ? (index) => setDeleteImageConfirm(index) : () => undefined}
      onMoveImage={canEdit ? handleMoveImage : () => undefined}
      onAddImage={canEdit ? () => setIsImageUploadModalOpen(true) : () => undefined}
      showcaseMode={showcaseMode}
      showcasePanel={showcaseMode}
      embedded={!showcaseMode}
    />
  ) : null;

  const mainSections = (
    <>
      {canEdit && !showcaseMode && activeGallery.length === 0 ? (
        <FeatureTip tipId="hero_photo" className="mb-4" enabled={canEdit}>
          A clear primary photo is the first thing people see on your public build. Add one when you are ready.
        </FeatureTip>
      ) : null}

      {canEdit && !showcaseMode && activeGallery.length > 0 && modifications.length === 0 ? (
        <FeatureTip tipId="import_mods" className="mb-4" enabled={canEdit}>
          Paste the list you already keep in Notes, or upload a spreadsheet. Bilt will organize it for you to review.
        </FeatureTip>
      ) : null}

      {/* Owner-only completeness nudge — never shown to public visitors. */}
      {isOwner && !showcaseMode && !profileCompletion.completed && (
        <section className="rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-sm font-medium text-gray-200">Keep documenting this build</p>
            <span className="font-mono text-xs text-gray-300 font-semibold tabular-nums">{profileCompletion.percent}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full rounded-full bg-orange-500/80 transition-all"
              style={{
                width: `${profileCompletion.percent}%`,
                ...(accentHex ? { backgroundColor: accentHex } : {}),
              }}
            />
          </div>
          {profileCompletion.missing.length > 0 && (
            <p className="text-xs text-gray-400 mt-2">
              Only you can see this. Add{' '}
              {profileCompletion.missing
                .slice(0, 3)
                .map((m) => COMPLETION_LABELS[m] || m)
                .join(', ')}
              {profileCompletion.missing.length > 3 ? '…' : ''} next.
            </p>
          )}
        </section>
      )}

      <div
        className={`grid grid-cols-1 gap-5 sm:gap-6 ${
          vehicle.engine ||
          vehicle.transmission ||
          vehicle.drivetrain ||
          vehicle.chassis ||
          vehicle.build_status
            ? 'lg:grid-cols-2 lg:gap-8 lg:items-stretch'
            : ''
        }`}
      >
      <section
        id="overview"
        className={`h-full scroll-mt-24 rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/80 ${
          showcaseMode ? 'p-6 sm:p-8' : 'p-5 sm:p-7'
        }`}
        style={
          accentHex
            ? {
                borderColor: `${accentHex}33`,
                boxShadow: `inset 3px 0 0 0 ${accentHex}`,
              }
            : undefined
        }
      >
        <div className="flex flex-col gap-6 h-full">
          <div className="min-w-0">
            <p className="kicker mb-3" style={accentHex ? { color: accentHex } : undefined}>
              Build profile
            </p>
            <h1
              className={`font-display font-semibold leading-[1.05] tracking-tight text-white ${
                showcaseMode
                  ? 'text-3xl sm:text-4xl md:text-5xl'
                  : 'text-2xl sm:text-3xl md:text-[2.5rem]'
              }`}
            >
              {vehicle.year}{' '}
              <span className="block sm:inline mt-0.5 sm:mt-0">
                {vehicle.make} {vehicle.model}
              </span>
              {vehicle.trim && (
                <span className="block text-lg sm:text-xl md:text-2xl font-normal text-gray-400 mt-1.5 tracking-normal">
                  {vehicle.trim}
                </span>
              )}
            </h1>
            {vehicle.nickname && (
              <p
                className={`mt-4 font-display italic font-medium ${
                  accentHex ? '' : 'text-orange-200/90'
                } ${showcaseMode ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`}
                style={accentHex ? { color: accentHex } : undefined}
              >
                &ldquo;{vehicle.nickname}&rdquo;
              </p>
            )}
            {(purposeChips.length > 0 || buildProfile?.build_stage) && (
              <ul className="mt-3 flex flex-wrap gap-2 list-none" aria-label="Build purpose and stage">
                {buildProfile?.build_stage ? (
                  <li
                    className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-gray-200 capitalize"
                    style={
                      accentHex
                        ? {
                            borderColor: `${accentHex}88`,
                            color: accentHex,
                            backgroundColor: `${accentHex}18`,
                          }
                        : undefined
                    }
                  >
                    {String(buildProfile.build_stage).replace(/_/g, ' ')}
                  </li>
                ) : null}
                {purposeChips.map((purpose) => (
                  <li
                    key={purpose}
                    className="rounded-lg border border-orange-500/25 bg-orange-500/[0.07] px-2.5 py-1 text-xs text-orange-200/90"
                    style={
                      accentHex
                        ? {
                            borderColor: `${accentHex}66`,
                            color: accentHex,
                            backgroundColor: `${accentHex}14`,
                          }
                        : undefined
                    }
                  >
                    {PURPOSE_LABELS[purpose] || purpose}
                  </li>
                ))}
              </ul>
            )}
            {(buildSummary || vehicle.description) && (
              <p
                className={`mt-4 text-gray-400 leading-relaxed max-w-2xl ${
                  showcaseMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                }`}
              >
                {buildSummary || vehicle.description}
              </p>
            )}
            <ExteriorFinishDisplay data={vehicle} className="mt-3 max-w-xl" />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-white/[0.06]">
            {owner.profile_image ? (
              <img
                src={owner.profile_image}
                alt=""
                className="w-11 h-11 rounded-full object-cover ring-1 ring-white/[0.08]"
              />
            ) : (
              <span className="w-11 h-11 rounded-full bg-[#111] flex items-center justify-center text-sm font-semibold text-gray-300 ring-1 ring-white/[0.08]">
                {(owner.display_name || owner.username).charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <p className="spec-label">Builder</p>
              <Link
                to={`/${owner.username}`}
                className="text-sm font-medium text-white hover:text-orange-200 transition-colors truncate block"
              >
                {owner.display_name || owner.username}
              </Link>
              <p className="font-mono text-xs text-gray-500">@{owner.username}</p>
            </div>
          </div>

          {settings.show_performance && performance && (
            <div className={`stat-ledger grid grid-cols-3 divide-x divide-white/[0.06]/60 ${showcaseMode ? 'max-w-md' : ''}`}>
              {performance.horsepower != null && (
                <div className="px-3 py-4 sm:py-5 text-center">
                  <div className={`font-display font-semibold tracking-tight text-white tabular-nums leading-none ${showcaseMode ? 'text-3xl' : 'text-2xl sm:text-[1.75rem]'}`}>
                    {performance.horsepower}
                  </div>
                  <div className="spec-label mt-2">Horsepower</div>
                </div>
              )}
              {performance.torque != null && (
                <div className="px-3 py-4 sm:py-5 text-center">
                  <div className={`font-display font-semibold tracking-tight text-white tabular-nums leading-none ${showcaseMode ? 'text-3xl' : 'text-2xl sm:text-[1.75rem]'}`}>
                    {performance.torque}
                  </div>
                  <div className="spec-label mt-2">Torque</div>
                </div>
              )}
              {performance.redline != null && (
                <div className="px-3 py-4 sm:py-5 text-center">
                  <div className={`font-display font-semibold tracking-tight text-white tabular-nums leading-none ${showcaseMode ? 'text-3xl' : 'text-2xl sm:text-[1.75rem]'}`}>
                    {performance.redline}
                  </div>
                  <div className="spec-label mt-2">Redline</div>
                </div>
              )}
            </div>
          )}

          {!showcaseMode && (
            <div className="flex flex-col gap-2 sm:max-w-md mt-auto">
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleShare}
                  className="btn-primary flex-1 min-w-[8rem]"
                >
                  <ShareIcon className="w-4 h-4" />
                  Share build
                </button>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  aria-label="Copy link"
                  title="Copy link"
                  className="btn-secondary min-w-[44px] px-3"
                >
                  {copied ? <CheckIcon className="w-4 h-4 text-emerald-400" /> : <LinkIcon className="w-4 h-4" />}
                </button>
                {!isOwner ? (
                  <>
                    <LikeBuildButton
                      vehicleId={vehicle.id}
                      initialLiked={isLiked}
                      initialLikeCount={likeCount}
                      size="md"
                      showCount
                      onLikeChange={(_liked, nextCount) => setLiveLikeCount(nextCount)}
                    />
                    <FavoriteBuildButton
                      vehicleId={vehicle.id}
                      initialFavorited={isFavorited}
                      initialFavoriteCount={favoriteCount}
                      size="md"
                      showCount
                      onFavoriteChange={(favorited, nextCount) => {
                        setLiveIsFavorited(favorited);
                        setLiveFavoriteCount(nextCount);
                      }}
                    />
                    <BuildWatchButton vehicleId={vehicle.id} isFavorited={isFavorited} />
                  </>
                ) : null}
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => setShareModalOpen(true)}
                    aria-label="QR code"
                    title="QR code & identity cards"
                    className="btn-secondary min-w-[44px] px-3"
                  >
                    <QrCodeIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              {copied && <p className="text-xs text-emerald-400/90" role="status">Link copied</p>}
            </div>
          )}
        </div>
      </section>

      {/* Build specs — technical spec grid (Bilt identity fields). */}
      <VehicleBuildSpecs
        chassis={vehicle.chassis}
        engine={vehicle.engine}
        transmission={vehicle.transmission}
        drivetrain={vehicle.drivetrain}
        buildStatus={vehicle.build_status}
        className={`h-full border border-white/[0.06] bg-[#0c0c0c]/80 ${showcaseMode ? 'p-6 sm:p-8' : 'p-5 sm:p-6'}`}
      />
      </div>

      {vehicle.custom_message && (
        <section
          className={`border-l-2 border-orange-500/50 bg-gradient-to-r from-orange-500/[0.07] to-transparent ${
            showcaseMode ? 'px-6 py-5 sm:px-8 sm:py-6' : 'px-5 py-4 sm:px-6 sm:py-5'
          }`}
        >
          <p className="kicker mb-2">
            Message from {owner.display_name || owner.username}
          </p>
          <p className={`text-gray-200 leading-relaxed ${showcaseMode ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}>
            {vehicle.custom_message}
          </p>
        </section>
      )}

      {(expansion?.stats_strip?.length ?? 0) > 0 ? (
        <div
          className={accentHex ? 'build-stats-accent' : undefined}
          style={accentHex ? ({ ['--build-accent' as string]: accentHex } as React.CSSProperties) : undefined}
          onFocus={() => {
            apiService.trackEvent('build_stats_viewed', { vehicle_id: vehicle.id });
          }}
        >
          <BuildStatsStrip stats={expansion!.stats_strip} />
        </div>
      ) : null}

      {vehicle?.id ? (
        <div id="recognition" className="scroll-mt-24">
          <VehicleRecognitionSection
            vehicleId={vehicle.id}
            isOwner={!!canEdit}
            awards={awards || []}
          />
        </div>
      ) : null}

      {achievements.length > 0 ? (
        <section
          className="space-y-3"
          aria-label="Platform achievements and milestones"
          onFocus={() => {
            apiService.trackEvent('achievement_opened', { vehicle_id: vehicle.id });
          }}
        >
          <AchievementCollection achievements={achievements} />
        </section>
      ) : null}

      {expansion?.documentation_score ? (
        <div
          onFocus={() => {
            apiService.trackEvent('documentation_score_opened', { vehicle_id: vehicle.id });
          }}
        >
          <DocumentationScore
            score={expansion.documentation_score}
            isOwner={!!canEdit}
            vehicleId={vehicle.id}
            onScoreUpdated={(score) => {
              setVehicleData((prev) =>
                prev?.expansion
                  ? { ...prev, expansion: { ...prev.expansion, documentation_score: score } }
                  : prev
              );
            }}
          />
        </div>
      ) : null}

      {showPerformanceSection ? (
        <section
          id="performance"
          className="space-y-4 scroll-mt-24"
          aria-label="Performance"
          onFocus={() => {
            apiService.trackEvent('performance_record_opened', { vehicle_id: vehicle.id });
          }}
        >
          {nonDynoRecords.length > 0 || expansion?.dyno_summary ? (
            <>
              {nonDynoRecords.length > 0 ? <PerformanceRecords records={nonDynoRecords} /> : null}
              {expansion?.dyno_summary ? <DynoHistory summary={expansion.dyno_summary} /> : null}
            </>
          ) : canEdit ? (
            <div className="rounded-2xl border border-dashed border-white/[0.08] bg-[#0c0c0c]/40 p-5 sm:p-6">
              <p className="kicker mb-2" style={accentHex ? { color: accentHex } : undefined}>
                Performance
              </p>
              <h2 className="heading-section text-xl mb-2">No records yet</h2>
              <p className="text-sm text-gray-400 mb-4 max-w-md">
                Add dyno, track, or drag results so visitors can see how this build performs.
              </p>
              <button
                type="button"
                className="btn-secondary btn-sm min-h-[44px]"
                onClick={() => setPerfManagerOpen(true)}
              >
                Add performance records
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {expansion?.factory_comparison ? (
        <div
          onFocus={() => {
            apiService.trackEvent('factory_comparison_opened', { vehicle_id: vehicle.id });
          }}
        >
          <FactoryCurrentComparison comparison={expansion.factory_comparison} />
        </div>
      ) : null}

      {canEdit && !showcaseMode && qrPrintKitEnabled && qrImageUrl && shareUrl ? (
        <section className="rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/40 p-4 sm:p-5" aria-label="QR print kit">
          <QrPrintKitPanel
            enabled
            vehicleName={vehicle.nickname || vehicleTitle}
            shareUrl={shareUrl}
            qrImageUrl={qrImageUrl}
            vehicleId={vehicle.id}
            vehicleImageUrl={activeGallery[0]?.src || null}
          />
        </section>
      ) : null}

      {showBuildSection && (
        <section className="space-y-4 md:space-y-5 scroll-mt-24" id="build">
          <div className="border-b border-white/[0.06] pb-4">
            <p className="kicker mb-2" style={accentHex ? { color: accentHex } : undefined}>
              Documented parts
            </p>
            <h2 className={`heading-section ${showcaseMode ? 'text-2xl' : 'text-xl sm:text-2xl'}`}>
              Build
            </h2>
            <p className={`font-mono text-xs text-gray-500 mt-1.5 tabular-nums ${showcaseMode ? 'text-sm' : ''}`}>
              {modifications.length} documented part{modifications.length === 1 ? '' : 's'}
              {showPricing && totalModCost > 0 && (
                <span className="text-gray-600"> · {formatCurrency(totalModCost)} invested</span>
              )}
            </p>
          </div>
          <VehicleBuildViews
            modifications={modifications.map((mod) => ({
              id: mod.id,
              title: mod.title,
              description: mod.description ?? '',
              category: mod.category,
              vendor: mod.vendor ?? '',
              cost: mod.cost,
              install_date: mod.install_date,
              notes: mod.notes,
              parent_modification_id: mod.parent_modification_id,
              parentComponent: mod.parentComponent,
              specifications: mod.specifications,
              sort_order: mod.sort_order,
              image_url: resolvePublicFileUrl(mod.image_url) ?? mod.image_url ?? null,
              image_id: mod.image_id ?? null,
            }))}
            showPricing={showPricing}
            compact={showcaseMode}
            syncUrl={!showcaseMode}
            autoCarShow={showcaseMode}
            defaultCollapseLarge
            canEdit={canEdit}
            vehicleLabel={vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            onEditModification={canEdit ? handleEditModification : undefined}
            onDeleteModification={canEdit ? handleDeleteModification : undefined}
            onAddChildModification={
              canEdit
                ? (parent) => openAddModification(parent)
                : undefined
            }
            onOrganizeMove={canEdit ? handleOrganizeMove : undefined}
            onOrganizeReorder={canEdit ? handleOrganizeReorder : undefined}
            onOrganizeBulkMove={canEdit ? handleOrganizeBulkMove : undefined}
            onOrganizeBulkCategory={canEdit ? handleOrganizeBulkCategory : undefined}
            onOrganizeBulkDelete={canEdit ? handleOrganizeBulkDelete : undefined}
            onSelectModeChange={canEdit ? setOrganizeSelectMode : undefined}
            emptyActions={
              canEdit && !showcaseMode ? (
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => openAddModification()}
                    className="btn-primary"
                  >
                    Add modification
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsImportModificationsModalOpen(true)}
                    className="btn-secondary"
                  >
                    Import build list
                  </button>
                </div>
              ) : null
            }
          />
        </section>
      )}

      {settings.show_images && activeGallery.length === 0 && !canEdit && (
        <section className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.02] py-12 text-center">
          <p className="text-gray-500 text-sm">No photos shared for this build yet.</p>
        </section>
      )}

      {(expansion?.media_albums?.length ?? 0) > 0 ? (
        <div id="media" className="scroll-mt-24">
          <MediaAlbums
            albums={expansion!.media_albums}
            vehicleId={vehicle.id}
            onOpenAlbum={() => {
              apiService.trackEvent('media_album_opened', { vehicle_id: vehicle.id });
            }}
          />
        </div>
      ) : null}

      <VehiclePassportStrip vehicleId={vehicle.id} />
      <VehiclePassportOwnerPanel vehicleId={vehicle.id} isOwner={Boolean(isOwner || canEdit)} />

      {showTimelineSection ? (
        <div id="timeline" className="scroll-mt-24">
          <BuildTimeline entries={history} loading={historyLoading} />
        </div>
      ) : null}

      {(expansion?.contributors?.length ?? 0) > 0 ? (
        <div
          onFocus={() => {
            apiService.trackEvent('contributor_opened', { vehicle_id: vehicle.id });
          }}
        >
          <ContributorCards contributors={expansion!.contributors} />
        </div>
      ) : null}

      {buildProfile?.owner_card_enabled ? (
        <div
          onFocus={() => {
            apiService.trackEvent('owner_card_opened', { vehicle_id: vehicle.id });
          }}
        >
          <OwnerIdentityCard
            card={buildProfile.owner_card}
            username={owner.username}
            avatarUrl={owner.profile_image || null}
            enabled
          />
        </div>
      ) : null}

      {vehicle?.id && owner?.username && (vehicle as any).is_public !== false ? (
        <OwnerOtherBuilds
          ownerUsername={owner.username}
          currentVehicleId={vehicle.id}
          ownerDisplayName={owner.display_name || owner.username}
        />
      ) : null}

      {/* Collectibility — secondary/AI estimate, moved to the bottom and
          collapsed by default so it does not read as the headline feature. */}
      {collectibility && (
        <section>
          <p className="text-meta mb-2">Extras</p>
          <CollectibilityPanel data={collectibility} compact defaultExpanded={false} />
        </section>
      )}

      {!showcaseMode && !canEdit && (
        <section className="relative overflow-hidden rounded-2xl ring-1 ring-white/5 bg-gradient-to-b from-[#0a0a0a]/70 to-[#0a0a0a]/60 p-8 sm:p-12 text-center">
          <BuiltLogoMark size="md" className="mx-auto mb-4" />
          <p className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white max-w-md mx-auto mb-2">
            Every build has a story.
          </p>
          <p className="text-muted text-sm sm:text-base max-w-md mx-auto mb-7 leading-relaxed">
            Start yours on Bilt.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto sm:max-w-none">
            <Link to="/register" className="btn-primary btn-lg sm:px-8">
              Start your garage
            </Link>
            <Link to="/events" className="btn-ghost btn-lg">
              Browse events
            </Link>
          </div>
        </section>
      )}
    </>
  );

  const heroMasthead = !showcaseMode ? (
    <div
      className={`flex flex-col gap-3 px-4 sm:px-5 py-4 ${
        heroPlacement === 'bottom_center'
          ? 'items-center text-center sm:flex-col'
          : 'sm:flex-row sm:items-end sm:justify-between'
      }`}
    >
      <div className="min-w-0">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.14em] mb-1 text-gray-400"
          style={accentHex ? { color: accentHex } : undefined}
        >
          {vehicle.year} {vehicle.make}
        </p>
        <p className="font-display font-semibold tracking-tight leading-tight text-xl sm:text-2xl text-white">
          {vehicle.nickname || `${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ''}`}
        </p>
      </div>
      <BuildEngagementBar
        favoriteCount={favoriteCount}
        likeCount={likeCount}
        viewCount={viewCount}
        milestoneCount={milestoneCount > 0 ? milestoneCount : null}
      />
    </div>
  ) : null;

  const galleryAndMain = (
    <div className="flex flex-col min-w-0">
      {showGallery ? (
        <div className="mb-5 sm:mb-6 md:mb-8 -mx-4 sm:-mx-6 lg:mx-0 lg:rounded-2xl lg:border lg:border-white/[0.06] lg:overflow-hidden bg-[#0c0c0c]/70">
          {/* Masthead sits beside the photos on the page surface — no overlay scrim on the images. */}
          {heroPlacement === 'top_left' ? (
            <div className="border-b border-white/[0.06]">{heroMasthead}</div>
          ) : null}
          {galleryBlock}
          {heroPlacement !== 'top_left' ? (
            <div className="border-t border-white/[0.06]">{heroMasthead}</div>
          ) : null}
        </div>
      ) : null}
      {!showGallery && !showcaseMode ? (
        <div className="mb-5 rounded-2xl border border-white/[0.06] bg-[#0c0c0c]/70 overflow-hidden">
          {heroMasthead}
        </div>
      ) : null}
      <main className="space-y-5 sm:space-y-6 md:space-y-8">{mainSections}</main>
    </div>
  );

  // overflow-x-clip (not hidden): clips horizontal bleed without breaking sticky sidebar.
  const pageRootClass = `page relative min-h-screen overflow-x-clip ${showcaseMode ? 'showcase-page' : ''} ${accentHex ? 'page-has-build-accent' : ''} ${canEdit && !isAnyModalOpen && !showcaseMode && !organizeSelectMode ? 'page-with-toolbar' : ''}`;
  const pageRootStyle: React.CSSProperties | undefined = accentHex
    ? ({
        ['--build-accent' as string]: accentHex,
      } as React.CSSProperties)
    : undefined;

  const pageBody = (
    <div className={pageRootClass} style={pageRootStyle} data-hero-treatment={heroTreatment} data-hero-placement={heroPlacement}>
      {!showcaseMode ? (
        <>
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: accentHex
                ? `radial-gradient(ellipse at top left, ${accentHex}18, transparent 55%)`
                : 'radial-gradient(ellipse at top left, rgba(249,115,22,0.07), transparent 55%)',
            }}
            aria-hidden
          />
          <div className="film-grain opacity-[0.035]" aria-hidden />
        </>
      ) : null}
      {canEdit && !isAnyModalOpen && !showcaseMode && !organizeSelectMode && (
        <VehicleOwnerToolbar
          onAddModification={() => openAddModification()}
          onImportModifications={() => setIsImportModificationsModalOpen(true)}
          onEnhanceModifications={() => setIsEnhanceModalOpen(true)}
          showEnhance={modifications.length > 0}
          onEditVehicle={() => setIsEditVehicleModalOpen(true)}
          onSettings={() => setIsSettingsModalOpen(true)}
        />
      )}

      {canEdit && vehicleData.is_super_admin_edit && !isOwner && !showcaseMode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <AdminEditingBanner contentLabel="vehicle" ownerLabel={`@${owner.username}`} />
        </div>
      )}

      <header className={showcaseMode ? 'showcase-header' : 'sticky top-0 z-40 border-b border-white/[0.06] bg-[#0a0a0a]/95'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          {showcaseMode ? (
            <>
              <div className="min-w-0 flex-1">
                <p className="text-meta text-orange-400/80 mb-0.5">Bilt build</p>
                <p className="text-sm sm:text-base font-semibold text-white truncate">{vehicleTitle}</p>
              </div>
              <Link
                to={`/${owner.username}`}
                className="flex items-center gap-2 shrink-0 text-sm text-gray-300 hover:text-white transition-colors"
              >
                {owner.profile_image ? (
                  <img
                    src={owner.profile_image}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover ring-1 ring-white/[0.12]"
                  />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-[#111] flex items-center justify-center text-xs font-bold text-orange-300">
                    {(owner.display_name || owner.username).charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="hidden sm:inline truncate max-w-[120px]">@{owner.username}</span>
              </Link>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                <Link
                  to={canEdit || isOwner ? '/garage' : '/'}
                  className="btn-icon text-gray-400 hover:text-white"
                  aria-label={canEdit || isOwner ? 'Back to Garage' : 'Back to Bilt home'}
                  title={canEdit || isOwner ? 'Garage' : 'Home'}
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </Link>
                <Link to="/" className="flex items-center gap-2 text-white shrink-0 min-h-[40px]">
                  <BuiltLogoMark size="sm" />
                  <span className="font-semibold text-sm">Bilt</span>
                </Link>
              </div>
              <Link
                to={`/${owner.username}`}
                className="flex items-center gap-2 min-w-0 text-sm text-gray-300 hover:text-white transition-colors"
              >
                {owner.profile_image ? (
                  <img
                    src={owner.profile_image}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover ring-1 ring-white/[0.12]"
                  />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-[#111] flex items-center justify-center text-xs font-bold text-orange-300">
                    {(owner.display_name || owner.username).charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="truncate">@{owner.username}</span>
              </Link>
            </>
          )}
        </div>
      </header>

      {showcaseMode && showGallery ? (
        <div className="showcase-split">
          <div className="showcase-split-gallery">{galleryBlock}</div>
          <div className="showcase-split-content">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8">
              {mainSections}
            </main>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-5 sm:py-6 md:py-8">
          {showcaseMode ? (
            galleryAndMain
          ) : (
            <VehicleSectionNav
              items={sectionNavItems}
              ownerTools={ownerToolActions}
              accentColor={accentHex}
            >
              {galleryAndMain}
            </VehicleSectionNav>
          )}
        </div>
      )}

      <ImageLightbox
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        images={activeGallery.map((img) => ({
          src: img.src,
          alt: img.alt,
          title: img.title,
        }))}
        currentIndex={lightboxIndex ?? 0}
        onNavigate={setLightboxIndex}
      />

      {canEdit && (
        <>
          {isAddModificationModalOpen && (
            <AddModificationModal
              isOpen={isAddModificationModalOpen}
              onClose={() => {
                setIsAddModificationModalOpen(false);
                setAddModificationParent(null);
              }}
              onSubmit={handleModificationAdded}
              existingModifications={editableMods as never}
              vendors={allVendors}
              refreshVendors={refreshVendors}
              initialParent={addModificationParent}
            />
          )}
          {isEditVehicleModalOpen && (
            <EditVehicleModal
              isOpen={isEditVehicleModalOpen}
              onClose={() => setIsEditVehicleModalOpen(false)}
              vehicle={{
                id: vehicle.id,
                year: vehicle.year,
                make: vehicle.make,
                model: vehicle.model,
                trim: vehicle.trim,
                color: vehicle.color,
                nickname: vehicle.nickname,
                description: vehicle.description,
                chassis: vehicle.chassis,
                engine: vehicle.engine,
                transmission: vehicle.transmission,
                drivetrain: vehicle.drivetrain,
                build_status: vehicle.build_status,
                exterior_finish_type: vehicle.exterior_finish_type,
                exterior_finish_color: vehicle.exterior_finish_color,
                exterior_finish_brand: vehicle.exterior_finish_brand,
                exterior_finish_style: vehicle.exterior_finish_style,
              } as Vehicle}
              onVehicleUpdated={handleVehicleUpdated}
            />
          )}
          {isSettingsModalOpen && (
            <SettingsModal
              isOpen={isSettingsModalOpen}
              onClose={() => setIsSettingsModalOpen(false)}
              settings={displaySettings}
              onSave={handleSettingsSave}
              onImportClick={() => setIsImportModificationsModalOpen(true)}
              onDeleteVehicle={handleDeleteVehicle}
              vehicle={{ id: vehicle.id } as Vehicle}
              vehicleName={vehicle.nickname || vehicleTitle}
              modifications={editableMods}
              isOwner={canEdit}
            />
          )}
          {isImageUploadModalOpen && (
            <VehicleImageUploadModal
              isOpen={isImageUploadModalOpen}
              onClose={() => setIsImageUploadModalOpen(false)}
              vehicleId={vehicle.id}
              heroImageCount={activeGallery.length}
              error={imageUploadError}
              onErrorClear={() => setImageUploadError(null)}
              onImageUploaded={handleImageUploaded}
              onError={setImageUploadError}
            />
          )}
          {isEditModificationModalOpen && editingModification && (
            <EditModificationModal
              isOpen={isEditModificationModalOpen}
              onClose={() => setIsEditModificationModalOpen(false)}
              modification={editingModification}
              onSubmit={handleModificationUpdated}
              existingModifications={editableMods as never}
              vendors={allVendors}
              refreshVendors={refreshVendors}
            />
          )}
          {isImportModificationsModalOpen && (
            <ImportModificationsModal
              vehicleId={vehicle.id}
              isOpen={isImportModificationsModalOpen}
              onClose={() => setIsImportModificationsModalOpen(false)}
              onImportComplete={async () => {
                await reloadEditableMods(vehicle.id);
                setIsImportModificationsModalOpen(false);
                showToast('Your build list has been added', 'success');
              }}
              vehicleInfo={{ make: vehicle.make, model: vehicle.model, year: vehicle.year }}
            />
          )}
          {isEnhanceModalOpen && (
            <EnhanceModificationsModal
              isOpen={isEnhanceModalOpen}
              vehicleId={vehicle.id}
              onClose={() => setIsEnhanceModalOpen(false)}
              onApplied={async () => {
                await reloadEditableMods(vehicle.id);
              }}
            />
          )}
          <ImageTitleEditModal
            isOpen={imageTitleEditIndex !== null}
            initialTitle={imageTitleEditIndex !== null ? activeGallery[imageTitleEditIndex]?.title || '' : ''}
            onClose={() => setImageTitleEditIndex(null)}
            onSave={handleSaveImageTitle}
          />
          {deleteImageConfirm !== null && (
            <div className="modal-overlay z-[9999]">
              <div className="modal-panel-sm">
                <h3 className="heading-card mb-2">Delete Image</h3>
                <p className="text-muted mb-4 text-sm">
                  Are you sure you want to delete this image? This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setDeleteImageConfirm(null)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void confirmDeleteImage();
                    }}
                    className="btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Owner-only QR & sharing settings (reuses the existing authed flow). */}
      {(isOwner || canEdit) && (
        <VehicleSharingModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          vehicleId={vehicle.id}
          vehicleName={vehicle.nickname || vehicleTitle}
        />
      )}

      {canEdit && !showcaseMode ? (
        <>
          <Modal
            isOpen={perfManagerOpen}
            onClose={() => setPerfManagerOpen(false)}
            title="Performance records"
            variant="sheet"
          >
            <PerformanceRecordManager
              vehicleId={vehicle.id}
              onChanged={() => {
                void refreshExpansion();
              }}
            />
          </Modal>
          <AlbumManagerModal
            isOpen={albumManagerOpen}
            onClose={() => setAlbumManagerOpen(false)}
            vehicleId={vehicle.id}
            galleryImages={galleryForManagers}
            onChanged={() => {
              void refreshExpansion();
            }}
          />
          <MediaLinkManagerModal
            isOpen={mediaLinkOpen}
            onClose={() => setMediaLinkOpen(false)}
            vehicleId={vehicle.id}
            galleryImages={galleryForManagers}
            modifications={modsForManagers}
            onChanged={() => {
              void refreshExpansion();
            }}
          />
          <BuildProfileEditorModal
            isOpen={profileEditorOpen}
            onClose={() => setProfileEditorOpen(false)}
            vehicleId={vehicle.id}
            onSaved={(profile) => {
              applyBuildProfile(profile);
              void refreshExpansion();
            }}
          />
          <ContributorRequestModal
            isOpen={contributorRequestOpen}
            onClose={() => setContributorRequestOpen(false)}
            vehicleId={vehicle.id}
            onCreated={() => {
              void refreshExpansion();
            }}
          />
          {shareCardsEnabled ? (
            <>
              <ShareCardGenerator
                isOpen={shareCardOpen}
                onClose={() => setShareCardOpen(false)}
                vehicle={{
                  year: vehicle.year,
                  make: vehicle.make,
                  model: vehicle.model,
                  trim: vehicle.trim,
                  nickname: vehicle.nickname,
                }}
                shareUrl={shareUrl}
                vehicleId={vehicle.id}
                ownerName={owner.display_name || owner.username}
                imageUrl={activeGallery[0]?.src || vehicleData.meta.image}
                qrImageUrl={qrImageUrl}
                stats={expansion?.stats_strip ?? []}
                achievements={achievements}
                records={expansion?.performance_records ?? []}
                awards={awards || []}
              />
              <QrStickerGenerator
                isOpen={qrStickerOpen}
                onClose={() => setQrStickerOpen(false)}
                vehicleName={vehicle.nickname || vehicleTitle}
                shareUrl={shareUrl}
                qrImageUrl={qrImageUrl || ''}
                vehicleId={vehicle.id}
                vehicleImageUrl={activeGallery[0]?.src || null}
              />
            </>
          ) : null}
        </>
      ) : null}

      {!showcaseMode && <Footer />}
    </div>
  );

  if (!isOwner && !showcaseMode) {
    return (
      <BuildViewTracker
        vehicleId={vehicle.id}
        source={referrer === 'qr' ? 'qr' : referrer || null}
      >
        {pageBody}
      </BuildViewTracker>
    );
  }

  return pageBody;
};

export default VehiclePage;
